import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const SESSION_KEY = "maison_luxe_chat_session";

function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

const SUGGESTIONS = [
  "I'm new to wigs — where do I start?",
  "Show me your best glueless wigs",
  "What's the difference between HD lace and 13x4?",
  "Track my order ML-XXXXXXXX",
  "Which wig works for thin hair?",
];

const GREETING = {
  role: "assistant",
  content:
    "Welcome to **Maison Luxe** 🤍 I'm your wig concierge — ask me anything about HD lace, glueless installs, lengths, styling or your order. How can I help today, love?",
};

// Lightweight markdown: **bold** and [label](path) → links
function renderRich(text) {
  if (!text) return null;
  const parts = [];
  // Split by markdown links
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let m;
  let i = 0;
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push({ type: "txt", value: text.slice(lastIndex, m.index), i: i++ });
    parts.push({ type: "link", label: m[1], href: m[2], i: i++ });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push({ type: "txt", value: text.slice(lastIndex), i: i++ });

  return parts.map((p) => {
    if (p.type === "link") {
      const isInternal = p.href.startsWith("/");
      if (isInternal) {
        return (
          <Link key={p.i} to={p.href} className="underline underline-offset-2 hover:text-gold">
            {p.label}
          </Link>
        );
      }
      return (
        <a key={p.i} href={p.href} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-gold">
          {p.label}
        </a>
      );
    }
    // bold + line breaks within text segment
    const seg = p.value.split(/(\*\*[^*]+\*\*|\n)/g).filter(Boolean);
    return seg.map((s, k) => {
      if (s === "\n") return <br key={`${p.i}-${k}`} />;
      if (s.startsWith("**") && s.endsWith("**"))
        return <strong key={`${p.i}-${k}`}>{s.slice(2, -2)}</strong>;
      return <span key={`${p.i}-${k}`}>{s}</span>;
    });
  });
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const sessionId = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    sessionId.current = getSessionId();
    // Load prior history
    api.get(`/chat/history/${sessionId.current}`).then((r) => {
      if (r.data && r.data.length) setMessages(r.data.map((m) => ({ role: m.role, content: m.content })));
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat/message", {
        session_id: sessionId.current,
        message: msg,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: `Sorry love, I had a hiccup just then. ${formatApiError(err)}` }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        data-testid="chat-launcher-button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Open chat concierge"
        className="fixed bottom-5 left-5 z-40 inline-flex items-center gap-2 rounded-full bg-charcoal text-ivory text-xs uppercase tracking-luxe px-5 py-3.5 shadow-2xl hover:bg-[#333] transition-all duration-300"
        style={{ boxShadow: "0 18px 48px -12px rgba(26,26,26,0.35)" }}
      >
        {open ? <X strokeWidth={1.5} className="w-4 h-4" /> : <Sparkles strokeWidth={1.5} className="w-4 h-4 text-gold" />}
        {open ? "Close" : "Concierge"}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 left-5 z-40 w-[calc(100vw-2.5rem)] sm:w-[400px] max-h-[78vh] bg-ivory border border-stone-line rounded-3xl shadow-2xl flex flex-col overflow-hidden fade-up"
          style={{ boxShadow: "0 24px 64px -16px rgba(26,26,26,0.25)" }}
        >
          <header className="px-5 py-4 border-b border-stone-line bg-nude flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-charcoal text-ivory flex items-center justify-center">
              <Sparkles strokeWidth={1.5} className="w-4 h-4 text-gold" />
            </div>
            <div className="flex-1">
              <p className="font-serif text-base leading-tight">Maison Luxe Concierge</p>
              <p className="small-label text-[10px]">AI-assisted · Mon–Sat 9–6 SAST</p>
            </div>
            <button data-testid="chat-close-button" onClick={() => setOpen(false)} className="p-1.5 hover:text-gold">
              <X strokeWidth={1.5} className="w-4 h-4" />
            </button>
          </header>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
            {!loaded ? (
              <p className="small-label text-center">Connecting…</p>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}-${i}`}>
                  <div
                    className={`max-w-[85%] text-sm leading-relaxed rounded-2xl px-4 py-3 ${
                      m.role === "user"
                        ? "bg-charcoal text-ivory rounded-br-md"
                        : "bg-nude text-charcoal rounded-bl-md"
                    }`}
                  >
                    {renderRich(m.content)}
                  </div>
                </div>
              ))
            )}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-nude text-charcoal rounded-2xl rounded-bl-md px-4 py-3 inline-flex gap-1">
                  <span className="w-1.5 h-1.5 bg-stone rounded-full animate-pulse" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-stone rounded-full animate-pulse" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-stone rounded-full animate-pulse" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {messages.length <= 1 && !busy && (
            <div className="px-5 pb-3 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  data-testid={`chat-suggestion-${s.slice(0, 12)}`}
                  onClick={() => send(s)}
                  className="text-[11px] uppercase tracking-luxe border border-stone-line rounded-full px-3 py-1.5 hover:bg-nude"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-stone-line px-4 py-3 flex items-center gap-2 bg-ivory"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about lengths, lace, orders…"
              disabled={busy}
              className="flex-1 bg-transparent text-sm border border-stone-line rounded-full px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-charcoal"
            />
            <button
              type="submit"
              data-testid="chat-send-button"
              disabled={busy || !input.trim()}
              className="w-10 h-10 rounded-full bg-charcoal text-ivory inline-flex items-center justify-center disabled:opacity-50"
            >
              <Send strokeWidth={1.5} className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
